import Loader from "components/loader";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import crossIcon from "../../assets/svg/crossBtn.svg";
import uploadIcon from "../../assets/svg/upload-icon.svg";
import {
  addLiveEvent,
  getLiveEventById,
  getVideoData,
  updateLiveEvents,
} from "../../axios/api";
import {
  convertUTCtoIST,
  extractVideoId,
  renderError,
  renderImagePreview,
} from "../../utils/utils";
import { toast } from "react-toastify";

const AddLiveEvents = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location?.state;

  const [liveEventData, setLiveEventData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const iconInputRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      thumbnailImage: null,
      thumbnailImageUrl: "",
      url: "",
      startTime: "",
      endTime: "",
    },
    onSubmit: async (values) => {
      // console.log("values", values);
      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("locale", "1");
        formData.append("title", values.title);
        formData.append("description", values.description);
        formData.append("url", values.url);
        // formData.append(
        //   "thumbnail_url",
        //   values.thumbnailImageUrl || values.thumbnailImage
        // );
        formData.append(
          "thumbnail_url",
          (values?.thumbnailImage && values.thumbnailImage) ||
            (values?.thumbnailImageUrl &&
              values.thumbnailImageUrl.replace(
                /(https?:\/\/)?(?:\d{1,3}\.){3}\d{1,3}/,
                ""
              )) ||
            ""
        );
        formData.append("live_video_id", values.title);
        formData.append("start_time", values.startTime.toString());
        formData.append("end_time", values.endTime.toString());

        // console.log("Form Data:", formData);
        let responseData;
        if (id) {
          responseData = await updateLiveEvents(formData, id);
          if (responseData?.data?.success) {
            toast.success("Live Events Updated Sucessfully");
            navigate("/admin/live-events");
          }
        } else {
          responseData = await addLiveEvent(formData);
          if (responseData?.data?.success) {
            toast.success("Live Events Added Sucessfully");
            navigate("/admin/live-events");
          }
        }

        if (!responseData?.data?.success) {
          toast.error(responseData?.data?.message);
        }
      } catch (error) {
        console.error("API Error:", error);
        toast.error("Something Went Wrong" + error);
      } finally {
        setLoading(false);
      }
    },
    validationSchema: yup.object({
      title: yup.string().trim().required("Video Title is required"),
      description: yup.string().required("Video description is required"),
      url: yup.string().required("Video url is required"),
      startTime: yup
        .string()
        .required("Start Date is required")
        .test(
          "is-after-min-date",
          "Start time cannot be earlier than the current date and time",
          function (value) {
            return new Date(value) >= new Date();
          }
        ),
      endTime: yup
        .string()
        .required("End Date is required")
        .test(
          "is-after-min-date",
          "End time cannot be earlier than the start date and time",
          function (value): any {
            return new Date(value) >= new Date(formik.values.startTime);
          }
        ),
    }),
  });

  const checkImage = useMemo(
    () =>
      renderImagePreview(
        formik.values.thumbnailImage,
        formik.values.thumbnailImageUrl
      ),
    [formik.values.thumbnailImage, formik.values.thumbnailImageUrl]
  );

  // Trigger fileInput on click on div
  const handleDivClick = (ref: any) => {
    ref.current.click();
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
  };

  const handleDrop = (e: any, fieldName: any) => {
    e.preventDefault();
    formik.setFieldValue(fieldName, e.dataTransfer.files[0]);
  };

  useEffect(() => {
    const fetchLiveEvent = async (id: string) => {
      try {
        setLoading(true);
        const liveEventResp = await getLiveEventById(id);
        setLiveEventData(liveEventResp?.data?.data);
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchLiveEvent(id);
    }
  }, [id]);

  useEffect(() => {
    if (liveEventData) {
      const ISTOffset = 5.5 * 60 * 60 * 1000;

      const utcDateStartTime = liveEventData.start_time;
      const utcDateEndTime = liveEventData.end_time;

      const startTime = new Date(utcDateStartTime);
      const endTime = new Date(utcDateEndTime);

      const ISTStartTime = new Date(startTime.getTime() + ISTOffset);
      const ISTEndTime = new Date(endTime.getTime() + ISTOffset);

      formik.setValues({
        title: liveEventData.title,
        description: liveEventData.description,
        thumbnailImage: null,
        thumbnailImageUrl: liveEventData.thumbnail_url,
        url: liveEventData.url,
        startTime: liveEventData.start_time
          ? new Date(ISTStartTime).toISOString().slice(0, 16)
          : "",
        endTime: liveEventData.end_time
          ? new Date(ISTEndTime).toISOString().slice(0, 16)
          : "",
      });
    }
  }, [liveEventData]);

  //   useEffect(() => {
  //     const getVideoDetails = async (videoId: string) => {
  //       try {
  //         setLoading(true);
  //         const videoDetailsResp = await getVideoData(videoId);
  //         formik.setValues((prevValues) => ({
  //           ...prevValues,
  //           title: videoDetailsResp?.data?.data?.youtubeData?.title || "",
  //           description:
  //             videoDetailsResp?.data?.data?.youtubeData?.description || "",
  //           thumbnailImage: null,
  //           thumbnailImageUrl:
  //             videoDetailsResp?.data?.data?.youtubeData?.thumbnail_url?.url || "",
  //         }));
  //       } catch (error) {
  //         console.log("error", error);
  //       } finally {
  //         setLoading(false);
  //       }
  //     };

  //     const videoId = extractVideoId(formik.values.url);
  //     if (videoId) {
  //       getVideoDetails(videoId);
  //     }
  //   }, [formik.values.url]);

  const getVideoDetails = async (e: any) => {
    e.preventDefault();
    try {
      setLoading(true);
      const videoId = extractVideoId(formik.values.url);
      if (videoId) {
        const videoDetailsResp = await getVideoData(videoId);
        formik.setValues((prevValues) => ({
          ...prevValues,
          title: videoDetailsResp?.data?.data?.youtubeData?.title || "",
          description:
            videoDetailsResp?.data?.data?.youtubeData?.description || "",
          thumbnailImage: null,
          thumbnailImageUrl:
            videoDetailsResp?.data?.data?.youtubeData?.thumbnail_url?.url || "",
        }));
      } else {
        toast.error("Wrong Youtube URL");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  // console.log("formik.values", formik.values);
  return (
    <div className="m-3 bg-[#FFFFFF] p-3">
      {loading ? (
        <Loader />
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="my-5 flex w-11/12 flex-col">
            <div className="flex w-[100%] items-center justify-between gap-4">
              <div className="my-2 flex min-w-[50%] flex-col items-start justify-center gap-2">
                <label htmlFor="title" className="text-xl font-medium">
                  Event Title
                </label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter Event title here"
                  className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                />
                {formik.touched.title && formik.errors.title ? (
                  <div className="text-lg text-red-500">
                    {renderError(formik.errors.title)}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex w-[100%] items-center justify-between gap-4">
              <div className="relative my-2 flex min-w-[50%] flex-col items-start justify-center gap-2">
                <label htmlFor="url" className="text-xl font-medium">
                  Event URL
                </label>
                <div className="relative w-[100%]">
                  <input
                    type="text"
                    name="url"
                    id="url"
                    value={formik.values.url}
                    onChange={(e) => {
                      const youtubeUrlPattern =
                        /^(https:\/\/www\.youtube\.com\/watch\?v=|https:\/\/youtu\.be\/)[\w-]{11}$/;

                      if (youtubeUrlPattern.test(e.target.value)) {
                        formik.setFieldValue("url", e.target.value);
                      } else {
                        toast.error("Please enter a valid Live Event URL.");
                      }
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="Enter Event URL here"
                    className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                  />
                  <button
                    onClick={(e) => getVideoDetails(e)}
                    className="absolute right-3 top-[15%] rounded bg-[#008BD2] px-2  text-xl font-bold text-white"
                  >
                    Fetch
                  </button>
                </div>
                {formik.touched.url && formik.errors.url ? (
                  <div className="text-lg text-red-500">
                    {renderError(formik.errors.url)}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="my-2 flex w-full flex-col items-start justify-center gap-2">
              <label htmlFor="startTime" className="text-xl font-medium">
                Start Date
              </label>
              <input
                aria-label="Date and time"
                type="datetime-local"
                name="startTime"
                id="startTime"
                value={formik.values.startTime}
                min={new Date().toISOString().slice(0, 16)}
                //value={(new Date(formik.values.ExpiryDate)).toISOString().slice(0, 16)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Expiry Date here"
                className="h-10 w-[40%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
              />
              {formik.touched.startTime && formik.errors.startTime ? (
                <div className="text-lg text-red-500">
                  {renderError(formik.errors.startTime)}
                </div>
              ) : null}
            </div>

            <div className="my-2 flex w-full flex-col items-start justify-center gap-2">
              <label htmlFor="endTime" className="text-xl font-medium">
                End Date
              </label>
              <input
                aria-label="Date and time"
                type="datetime-local"
                name="endTime"
                id="endTime"
                value={formik.values.endTime}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                min={new Date().toISOString().slice(0, 16)}
                placeholder="Enter Expiry Date here"
                className="h-10 w-[40%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
              />
              {formik.touched.endTime && formik.errors.endTime ? (
                <div className="text-lg text-red-500">
                  {renderError(formik.errors.endTime)}
                </div>
              ) : null}
            </div>
            <div className="my-5 flex items-center justify-evenly gap-5">
              <div className="w-[35%]">
                <label htmlFor="description" className=" text-xl font-medium">
                  Event Description
                </label>
                <div className="relative my-3 mb-5 flex w-[100%] items-center justify-between">
                  <textarea
                    rows={6}
                    cols={50}
                    name="description"
                    id="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter Event description here"
                    className=" w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                  />
                </div>
                {formik.touched.description && formik.errors.description ? (
                  <div className="text-lg text-red-500">
                    {renderError(formik.errors.description)}
                  </div>
                ) : null}
              </div>

              <div className="w-[35%]">
                <label
                  htmlFor="thumbnailImageUrl"
                  className="text-xl font-medium"
                >
                  Event Thumbnail
                </label>
                {/* Icon Image Upload */}
                <div className="relative my-3 mb-5 flex w-[100%] items-center justify-between">
                  <input
                    //className="absolute bottom-0 left-8 -z-10 w-20"
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    required={
                      !formik.values.thumbnailImageUrl &&
                      !formik.values.thumbnailImage
                    }
                    name="thumbnailImageUrl"
                    id="thumbnailImageUrl"
                    ref={iconInputRef}
                    type="file"
                    accept=".jpg, .jpeg, .png, .gif"
                    onChange={(e) => {
                      formik.setFieldValue("thumbnailImage", e.target.files[0]);
                    }}
                  />
                  <div
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, "thumbnailImage")}
                    onClick={() => handleDivClick(iconInputRef)}
                    //className="flex h-44 w-full items-center justify-center rounded border-4 border-dashed"
                    className="relative flex h-44 w-full items-center justify-center overflow-hidden rounded border-4 border-dashed"
                  >
                    {formik.values.thumbnailImageUrl ||
                    formik.values.thumbnailImage ? (
                      <>
                        <div className="relative flex items-center p-2">
                          {(formik.values.thumbnailImage ||
                            formik.values.thumbnailImageUrl) && (
                            <img
                              className=""
                              alt="thumbnailImage"
                              src={checkImage}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation(); // Stop event propagation
                            formik.setFieldValue("thumbnailImage", null);
                            formik.setFieldValue("thumbnailImageUrl", "");
                            iconInputRef.current.value = null;
                          }}
                          className="absolute right-1 top-1 z-10"
                        >
                          <img src={crossIcon} alt="Cross Icon" />
                        </button>
                      </>
                    ) : (
                      <div
                        id="imageDiv"
                        className="flex flex-col items-center justify-center text-center font-medium hover:cursor-pointer"
                      >
                        <img
                          className="mb-2 w-1/4"
                          src={uploadIcon}
                          alt="Upload Icon"
                        />
                        <span>
                          <span className="text-blue-500">Click here </span>to
                          Upload
                        </span>
                        <span>Event Thumbnail</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <button
                name="submit"
                className="float-right my-5 w-24 rounded bg-[#008BD2] p-2 text-xl font-bold text-white"
                type="submit"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddLiveEvents;
